import React, {useCallback, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {auth} from '../../firebase.ts'
import {BoardFilters, LostReason, PipelineId, taskPipeline, TasksQuery} from '../../domain/entities/Pipeline.ts'
import {
    CONVERSION_LOST_REASONS,
    ConversionBoardFilters,
    ConversionLostReason,
    ConversionStage,
} from '../../domain/entities/ConversionPipeline.ts'
import {useCrmMutations, useCrmTeam} from '../../hooks/usePipelines.ts'
import {ActivationBoard} from './ActivationBoard.tsx'
import {ConversionBoard} from './ConversionBoard.tsx'
import {MyTasks} from './MyTasks.tsx'
import {ActivationDetailPanel} from './ActivationDetailPanel.tsx'
import {ConversionDetailPanel} from './ConversionDetailPanel.tsx'
import {MarkLostDialog} from './PipelineDialogs.tsx'
import {OverrideStageDialog} from './ConversionDialogs.tsx'
import {useConversionCtaRunner} from './useConversionCtaRunner.tsx'
import {newCommandId} from './pipelineView.ts'

export type PipelinesView = 'board' | 'tasks'

type Selected = {pipeline: PipelineId; id: string}
type LostRequest =
    | {pipeline: 'activation'; id: string; reason: LostReason | null; allowedReasons?: LostReason[]}
    | {pipeline: 'conversion'; id: string; reason: ConversionLostReason | null; allowedReasons?: ConversionLostReason[]}

const PIPELINE_STORAGE_KEY = 'pipelines.board'

function readStoredPipeline(): PipelineId {
    try {
        return window.localStorage.getItem(PIPELINE_STORAGE_KEY) === 'conversion' ? 'conversion' : 'activation'
    } catch {
        return 'activation'
    }
}

// Internal "Pipelines" section (admin only): the Activation and Conversion
// boards and My tasks, each opening its pipeline's detail panel.
export const PipelinesContainer: React.FC<{view: PipelinesView; onViewChange: (view: PipelinesView) => void}> = ({view, onViewChange}) => {
    const {t} = useTranslation('dashboard')
    const {team} = useCrmTeam()
    const {sendCrmCommand, sendConversionCommand} = useCrmMutations()
    const [pipeline, setPipeline] = useState<PipelineId>(readStoredPipeline)
    const [filters, setFilters] = useState<BoardFilters>({})
    const [conversionFilters, setConversionFilters] = useState<ConversionBoardFilters>({})
    const [tasksQuery, setTasksQuery] = useState<TasksQuery>({assignee: 'me'})
    const [selected, setSelected] = useState<Selected | null>(null)
    const [lostRequest, setLostRequest] = useState<LostRequest | null>(null)
    const [overrideRequest, setOverrideRequest] = useState<{id: string; currentStage: ConversionStage} | null>(null)
    // Conversion CTAs (card and panel) and their forms live here, once.
    const {runCta, isRunning: isCtaRunning, isBusy: ctaBusy, dialogs: ctaDialogs} = useConversionCtaRunner()
    const returnFocus = useRef<HTMLElement | null>(null)
    const currentUserId = auth.currentUser?.uid ?? null

    const changePipeline = (next: PipelineId) => {
        setPipeline(next)
        try { window.localStorage.setItem(PIPELINE_STORAGE_KEY, next) } catch { /* private mode: not remembered */ }
    }

    const openPanel = (next: Selected) => {
        if (!selected) returnFocus.current = document.activeElement as HTMLElement | null
        setSelected(next)
    }

    const closePanel = useCallback(() => {
        setSelected(null)
        const target = returnFocus.current
        returnFocus.current = null
        if (target?.isConnected) setTimeout(() => target.focus(), 0)
    }, [])

    const submitLost = (reason: string, note: string) => {
        if (!lostRequest) return Promise.resolve()
        const base = {commandId: newCommandId(), type: 'MARK_LOST' as const, note: note || undefined}
        const sent: Promise<unknown> = lostRequest.pipeline === 'conversion'
            ? sendConversionCommand({id: lostRequest.id, command: {...base, data: {reason: reason as ConversionLostReason}}})
            : sendCrmCommand({id: lostRequest.id, command: {...base, data: {reason: reason as LostReason}}})
        return sent.then(() => enqueueSnackbar(t('pipelines.feedback.lost', {reason: t(`pipelines.lostReason.${reason}`)}), {variant: 'success'}))
    }

    const tabClass = (active: boolean) => `h-9 px-4 rounded-full text-[13px] font-medium transition-colors duration-200 ease-out ${
        active ? 'bg-neutralBlack-900 text-white' : 'text-neutralBlack-700 hover:bg-neutralBlack-200 hover:text-neutralBlack-900'}`

    return (
        <div className="h-full min-h-0 flex flex-col gap-4">
            <div className="flex items-center flex-wrap gap-x-4 gap-y-3 flex-none">
                <h1 className="text-[28px] leading-[1.15] font-semibold tracking-[-.02em] text-neutralBlack-900">{t('pipelines.title')}</h1>
                {/* The board's pipeline; My tasks has its own pipeline filter. */}
                {view === 'board' && (
                    <label className="inline-flex items-center">
                        <span className="sr-only">{t('pipelines.pipeline')}</span>
                        <select value={pipeline} onChange={(e) => changePipeline(e.target.value as PipelineId)}
                                className="h-9 pl-3 pr-8 rounded-full border border-neutralBlack-200 bg-white text-[13px] font-semibold text-neutralBlack-900">
                            <option value="activation">{t('pipelines.activation')}</option>
                            <option value="conversion">{t('pipelines.conversion.name')}</option>
                        </select>
                    </label>
                )}
                <div role="tablist" aria-label={t('pipelines.title')} className="inline-flex items-center gap-1 p-1 rounded-full bg-neutralBlack-100">
                    <button type="button" role="tab" id="pl-tab-board" aria-controls="pl-tabpanel" aria-selected={view === 'board'}
                            onClick={() => onViewChange('board')} className={tabClass(view === 'board')}>
                        {pipeline === 'conversion' ? t('pipelines.tab.conversionBoard') : t('pipelines.tab.board')}
                    </button>
                    <button type="button" role="tab" id="pl-tab-tasks" aria-controls="pl-tabpanel" aria-selected={view === 'tasks'}
                            onClick={() => onViewChange('tasks')} className={tabClass(view === 'tasks')}>
                        {t('pipelines.tab.tasks')}
                    </button>
                </div>
            </div>

            <div id="pl-tabpanel" role="tabpanel" aria-labelledby={view === 'board' ? 'pl-tab-board' : 'pl-tab-tasks'}
                 className="flex-1 min-h-0 flex flex-col">
                {view === 'tasks' ? (
                    <MyTasks query={tasksQuery} onQueryChange={setTasksQuery} team={team} selectedId={selected?.id ?? null}
                             onOpenTask={(task) => openPanel({pipeline: taskPipeline(task), id: task.card.id})}/>
                ) : pipeline === 'conversion' ? (
                    <ConversionBoard filters={conversionFilters} onFiltersChange={setConversionFilters} team={team}
                                     selectedId={selected?.pipeline === 'conversion' ? selected.id : null}
                                     onOpenCard={(card) => openPanel({pipeline: 'conversion', id: card.id})}
                                     onRunCta={runCta} isCtaRunning={isCtaRunning} ctaBusy={ctaBusy}
                                     onRequestOverride={(card) => setOverrideRequest({id: card.id, currentStage: card.crmStage})}
                                     onRequestLost={(card) => setLostRequest({pipeline: 'conversion', id: card.id, reason: null})}/>
                ) : (
                    <ActivationBoard filters={filters} onFiltersChange={setFilters} team={team}
                                     selectedId={selected?.pipeline === 'activation' ? selected.id : null}
                                     onOpenCard={(card) => openPanel({pipeline: 'activation', id: card.id})}
                                     onRequestLost={(card, reason) => setLostRequest({pipeline: 'activation', id: card.id, reason})}/>
                )}
            </div>

            {selected?.pipeline === 'activation' && (
                <ActivationDetailPanel activationId={selected.id} team={team} currentUserId={currentUserId} onClose={closePanel}
                                       onRequestLost={(id, reason, allowedReasons) => setLostRequest({pipeline: 'activation', id, reason, allowedReasons})}/>
            )}
            {selected?.pipeline === 'conversion' && (
                <ConversionDetailPanel conversionId={selected.id} team={team} currentUserId={currentUserId} onClose={closePanel}
                                       onRunCta={runCta} isCtaRunning={isCtaRunning} ctaBusy={ctaBusy}
                                       onRequestOverride={(id, currentStage) => setOverrideRequest({id, currentStage})}
                                       onRequestLost={(id, reason, allowedReasons) => setLostRequest({pipeline: 'conversion', id, reason, allowedReasons})}/>
            )}

            <MarkLostDialog request={lostRequest}
                            allowedReasons={lostRequest?.allowedReasons?.length ? lostRequest.allowedReasons
                                : lostRequest?.pipeline === 'conversion' ? CONVERSION_LOST_REASONS : undefined}
                            onClose={() => setLostRequest(null)} onSubmit={submitLost}/>
            <OverrideStageDialog request={overrideRequest} onClose={() => setOverrideRequest(null)}
                                 onSubmit={(stage, note) => overrideRequest
                                     ? sendConversionCommand({id: overrideRequest.id, command: {commandId: newCommandId(), type: 'OVERRIDE_STAGE', note, data: {stage}}})
                                         .then(() => enqueueSnackbar(t('pipelines.feedback.overridden', {stage: t(`pipelines.stage.${stage}`)}), {variant: 'success'}))
                                     : Promise.resolve()}/>
            {ctaDialogs}
        </div>
    )
}
